import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API from '../services/api';
import { fetchInvestigationForIncident, resolveInvestigationStatus } from '../utils/incidentHelpers';
import InvestigationStatusTracker from './InvestigationStatusTracker';
import './IncidentDetail.css';

const IncidentDetail = () => {
  const { incidentId } = useParams();
  const navigate = useNavigate();
  const [incident, setIncident] = useState(null);
  const [investigation, setInvestigation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchIncidentDetails = async () => {
      try {
        setLoading(true);
        
        // Fetch incident details
        const incidentResponse = await API.get(`/api/incidents/${incidentId}`);
        
        if (!incidentResponse.data.success) {
          throw new Error(incidentResponse.data.message || 'Failed to fetch incident');
        }
        
        setIncident(incidentResponse.data.incident);
        
        // Fetch investigation details using the canonical helper (prefers canonical investigation row)
        try {
          const inv = await fetchInvestigationForIncident(incidentId);
          if (inv) {
            setInvestigation(inv);
          }
        } catch (e) {
          console.warn('Failed to fetch investigation via helper for', incidentId, e && e.message);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching incident details:', err);
        setError(err.message || 'An error occurred while fetching incident details');
        setLoading(false);
      }
    };

    fetchIncidentDetails();
  }, [incidentId]);

  if (loading) {
    return <div className="loading">Loading incident details...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <h2>Error</h2>
        <p>{error}</p>
        <button onClick={() => navigate('/incident-reports')}>Back to Incident Reports</button>
      </div>
    );
  }

  if (!incident) {
    return (
      <div className="not-found-container">
        <h2>Incident Not Found</h2>
        <p>The incident you're looking for doesn't exist or you don't have permission to view it.</p>
        <button onClick={() => navigate('/incident-reports')}>Back to Incident Reports</button>
      </div>
    );
  }

  return (
    <div className="incident-detail-container">
      <div className="incident-header">
        <h1>Incident #{incident.ID}</h1>
        <div className="incident-meta">
          <span className={`severity-badge severity-${incident.SEVERITY?.toLowerCase()}`}>
            {incident.SEVERITY}
          </span>
          <span className="incident-date">{new Date(incident.DATE).toLocaleDateString()}</span>
        </div>
      </div>

      <div className="incident-content">
        <div className="incident-section">
          <h2>Incident Details</h2>
          <div className="detail-row">
            <div className="detail-label">Title:</div>
            <div className="detail-value">{incident.TITLE}</div>
          </div>
          <div className="detail-row">
            <div className="detail-label">Department:</div>
            <div className="detail-value">{incident.PRIMARY_DEPARTMENT}</div>
          </div>
          <div className="detail-row">
            <div className="detail-label">Location:</div>
            <div className="detail-value">{incident.LOCATION}</div>
          </div>
          <div className="detail-row">
            <div className="detail-label">Description:</div>
            <div className="detail-value description">{incident.DESCRIPTION}</div>
          </div>
          
          {/* Add more incident details as needed */}
        </div>

        {investigation && (
          <div className="investigation-section">
            <h2>Investigation</h2>
            
            {/* Status Tracker */}
            <InvestigationStatusTracker 
              incidentId={incidentId}
              status={investigation.INVESTIGATION_STATUS}
              delegateEmail={investigation.DELEGATE_EMAIL}
              timestamps={{
                created: incident.CREATED_AT,
                delegateAssigned: investigation.DELEGATE_ASSIGNED,
                delegateCompleted: investigation.DELEGATE_COMPLETED_AT,
                supervisorReview: investigation.SUPERVISOR_REVIEW_DATE,
                managerReview: investigation.MANAGER_REVIEW_DATE,
                completed: investigation.COMPLETED_AT
              }}
              onSignOff={(role) => {
                // Implement sign-off modal/functionality here
                console.log(`Signing off as ${role}`);
              }}
            />
            
            {/* Display investigation details */}
            {investigation.INVESTIGATION_STATUS !== 'PENDING' && (
              <div className="investigation-details">
                <h3>Investigation Details</h3>
                
                <div className="detail-row">
                  <div className="detail-label">Root Cause:</div>
                  <div className="detail-value">{investigation.ROOT_CAUSE || 'Not specified'}</div>
                </div>
                
                {investigation.DELEGATE_EMAIL && (
                  <div className="detail-row">
                    <div className="detail-label">Delegate:</div>
                    <div className="detail-value">{investigation.DELEGATE_EMAIL}</div>
                  </div>
                )}
                
                {investigation.DELEGATE_FINDINGS && (
                  <div className="detail-row">
                    <div className="detail-label">Delegate Findings:</div>
                    <div className="detail-value">{investigation.DELEGATE_FINDINGS}</div>
                  </div>
                )}
                
                {/* Add more investigation details as needed */}
              </div>
            )}
            
            {/* Corrective Actions Section */}
            {investigation.CORRECTIVE_ACTIONS && (
              <div className="corrective-actions-section">
                <h3>Corrective Actions</h3>
                <table className="corrective-actions-table">
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>Assigned To</th>
                      <th>Target Date</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {investigation.CORRECTIVE_ACTIONS.map((action, index) => (
                      <tr key={index}>
                        <td>{action.DESCRIPTION}</td>
                        <td>{action.ASSIGNED_TO}</td>
                        <td>{new Date(action.TARGET_DATE).toLocaleDateString()}</td>
                        <td className={`status-${action.STATUS?.toLowerCase()}`}>{action.STATUS}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default IncidentDetail;